import { createContext, useContext, useState, useEffect } from 'react';
import { authFetch } from '../utils/api';
import { UrlDev } from '../url';
import { companyName as defaultCompanyName, companyDetails as defaultCompanyDetails } from '../configs';

const CompanyContext = createContext();

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

export const CompanyProvider = ({ children }) => {
  const [companyData, setCompanyData] = useState({
    name: defaultCompanyName || '',
    details1: defaultCompanyDetails?.details1 || '',
    details2: defaultCompanyDetails?.details2 || '',
    details3: defaultCompanyDetails?.details3 || '',
    logo_path: '/assets/company_logo.png',
    timezone: 'America/Toronto'
  });
  const [loading, setLoading] = useState(true);

  const fetchCompanyData = async () => {
    try {
      const data = await authFetch('/company/');
      if (data) {
        setCompanyData({
          name: data.name || defaultCompanyName || '',
          details1: data.details1 || defaultCompanyDetails?.details1 || '',
          details2: data.details2 || defaultCompanyDetails?.details2 || '',
          details3: data.details3 || defaultCompanyDetails?.details3 || '',
          logo_path: data.logo_path || '/assets/company_logo.png',
          timezone: data.timezone || 'America/Toronto'
        });
      }
    } catch (error) {
      console.error('Error loading company data:', error);
      // Keep default values on error - don't throw, just use defaults
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch if user might be authenticated (check for token)
    const token = document.cookie.match(/access_token=([^;]+)/);
    if (token) {
      fetchCompanyData();
    } else {
      // Not authenticated, just use defaults
      setLoading(false);
    }
  }, []);

  // Get the full logo URL
  const getLogoUrl = () => {
    if (companyData.logo_path.startsWith('/images/')) {
      return UrlDev + companyData.logo_path;
    }
    return companyData.logo_path;
  };

  const value = {
    companyData,
    loading,
    logoUrl: getLogoUrl(),
    refresh: fetchCompanyData
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};
